import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/lib/auth";

const NAV_LINKS = [
  { label: "Características", href: "#caracteristicas" },
  { label: "Cómo funciona", href: "#como-funciona" },
  { label: "Precios", href: "#precios" },
  { label: "Universidades", href: "#universidades" },
];

export default function Navbar() {
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? "bg-paper/85 backdrop-blur-xl border-b border-rule"
          : "bg-paper/60 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        <a
          href="/"
          data-testid="nav-logo"
          className="flex items-baseline gap-1 group"
          aria-label="unaTESIS inicio"
        >
          <span className="font-serif italic text-2xl text-copper -mr-0.5 group-hover:text-forest transition-colors">
            una
          </span>
          <span className="font-serif text-2xl tracking-tight text-ink">
            TESIS
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-9" data-testid="nav-links">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.replace("#", "")}`}
              className="text-[13px] tracking-wide text-ink-soft hover:text-forest transition-colors relative after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-copper hover:after:w-full after:transition-all after:duration-300"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <Link
              to={user.role === "admin" ? "/admin" : "/dashboard"}
              data-testid="nav-dashboard-btn"
              className="inline-flex items-center px-5 py-2.5 rounded-full text-[13px] font-medium text-paper bg-forest hover:bg-forest-dark transition-all shadow-sm hover:shadow-md hover:-translate-y-[1px]"
            >
              {user.role === "admin" ? "Panel admin" : "Mi escritorio"}
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="nav-login-btn"
                className="text-[13px] tracking-wide text-ink-soft hover:text-forest transition-colors px-3 py-2"
              >
                Iniciar sesión
              </Link>
              <Link
                to="/signup"
                data-testid="nav-cta-btn"
                className="inline-flex items-center px-5 py-2.5 rounded-full text-[13px] font-medium text-paper bg-forest hover:bg-forest-dark transition-all shadow-sm hover:shadow-md hover:-translate-y-[1px]"
              >
                Empezar gratis
              </Link>
            </>
          )}
        </div>

        <button
          aria-label="Abrir menú"
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          className="md:hidden p-2 -mr-2 text-ink"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="md:hidden border-t border-rule bg-paper/95 backdrop-blur-xl"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-sm text-ink hover:text-forest transition-colors"
              >
                {l.label}
              </a>
            ))}
            <div className="pt-2 flex flex-col gap-3">
              {user ? (
                <Link
                  to={user.role === "admin" ? "/admin" : "/dashboard"}
                  onClick={() => setOpen(false)}
                  data-testid="nav-mobile-dashboard"
                  className="w-full inline-flex items-center justify-center px-5 py-3 rounded-full text-sm font-medium text-paper bg-forest"
                >
                  {user.role === "admin" ? "Panel admin" : "Mi escritorio"}
                </Link>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setOpen(false)}
                    data-testid="nav-mobile-login"
                    className="w-full text-left text-sm text-ink-soft py-2"
                  >
                    Iniciar sesión
                  </Link>
                  <Link
                    to="/signup"
                    onClick={() => setOpen(false)}
                    data-testid="nav-mobile-cta"
                    className="w-full inline-flex items-center justify-center px-5 py-3 rounded-full text-sm font-medium text-paper bg-forest"
                  >
                    Empezar gratis
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
